/**
 * @file Type utility functions.
 * @author andreas.z.hellstrom@teliacompany.com (Andreas Hellström)
 */

//------------------------------------------------------------------------------
// Exported functions
//------------------------------------------------------------------------------

/**
 * Checks if object is of JavaScript String type.
 * Source: https://stackoverflow.com/questions/4059147/check-if-a-variable-is-a-string-in-javascript
 * @param {*} obj
 * @returns {boolean}
 */
export function isString(obj) {
  return typeof obj === 'string' || obj instanceof String;
}

/**
 * Checks if object is of JavaScript Number type.
 * Source: https://stackoverflow.com/questions/25176459/is-number-isnan-more-broken-than-isnan
 * @param {*} obj
 * @returns {boolean}
 */
export function isNumber(obj) {
  return typeof obj === 'number' && !Number.isNaN(obj);
}

/**
 * Checks if object is of JavaScript Number type and object value is an Integer.
 * @param {*} value
 * @returns {boolean}
 */
export function isInteger(value) {
  return isNumber(value) && value % 1 === 0;
}

/**
 * Checks if object is of JavaScript Date type.
 * Source: https://stackoverflow.com/questions/643782/how-to-check-whether-an-object-is-a-date
 * @param {*} obj
 * @returns {boolean}
 */
export function isDate(obj) {
  return Object.prototype.toString.call(obj) === '[object Date]';
}

/**
 * Checks if object is of true JavaScript Object type.
 * Should only return true for true objects, not other types that also are considered being of
 * type Object (like Date, Array, etc).
 * @param {*} obj
 * @returns {boolean}
 */
export function isObject(obj) {
  return (
    typeof obj === 'object' && obj !== null && obj.toString && obj.toString() === '[object Object]'
  );
}

/**
 * Check if object is defined.
 * Source: https://stackoverflow.com/questions/5113374/javascript-check-if-variable-exists-is-defined-initialized
 * @param {*} obj
 * @returns {boolean} Returns true if object is defined
 */
export function isDefined(obj) {
  return typeof obj !== 'undefined';
}

/**
 * Checks if object is undefined.
 * @param {*} obj
 * @returns {boolean}
 */
export function isUndefined(obj) {
  return typeof obj === 'undefined';
}

/**
 * Check if a object is either null or undefined.
 * Source: https://stackoverflow.com/questions/2647867/how-to-determine-if-variable-is-undefined-or-null
 * @param {*} obj
 * @returns {boolean} Returns true if object is null or undefined.
 */
export function isNullOrUndefined(obj) {
  // Because null == undefined is true, the below code will catch both null and undefined.
  return obj == null;
}
